// Module 3 - AnalysisManager.jsx - Analyse Sémantique Complète (~450 lignes)

import React, { useState, useEffect, useCallback, useRef } from 'react';
import { serverTimestamp } from 'firebase/firestore';
import SemanticAnalyzerService from './SemanticAnalyzerService';
import { 
  Lightbulb, 
  Loader2,
  Search, 
  CheckCircle, 
  MessageCircle,
  Save,
  RefreshCw,
  Sparkles,
  ExternalLink,
  BookOpen,
  Zap,
  AlertCircle
} from 'lucide-react';

/**
 * Composant AnalysisManager
 * Responsabilités : Analyse sémantique complète, validation, génération requête
 */
const AnalysisManager = ({
  userObjective,
  competenceId,
  conversationData,
  analysisData,
  competenceData,
  onAnalysisUpdate,
  onConversationUpdate,
  onSaveData,
  onLaunchSearch,
  onSwitchTab
}) => {
  // États locaux
  const [isLoading, setIsLoading] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [saveStatus, setSaveStatus] = useState(null);
  const [lastSaveTime, setLastSaveTime] = useState(null);
  const [error, setError] = useState(null);

  // Référence pour éviter les analyses multiples
  const analysisAttempted = useRef(false);

  // Effectuer l'analyse sémantique
  const performSemanticAnalysis = useCallback(async () => {
    if (!userObjective || analysisAttempted.current) return null;

    setIsLoading(true);
    setError(null);
    analysisAttempted.current = true;

    try {
      console.log('Analyse sémantique de l\'objectif:', userObjective);

      const conversationContext = conversationData.messages
        ?.filter(m => m.type === 'user')
        .map(m => m.text)
        .join("\n") || '';

      const analysisResults = await SemanticAnalyzerService.analyzeObjectiveWithAI(
        userObjective, 
        conversationContext
      );

      console.log('Résultats de l\'analyse sémantique:', analysisResults);

      // Mettre à jour les données d'analyse
      onAnalysisUpdate({
        results: analysisResults,
        completed: true
      });

      // Sauvegarder l'analyse
      const saveResult = await saveSemanticAnalysis(analysisResults, {
        conversationLength: conversationData.messages?.length || 0,
        contextSummary: conversationContext.substring(0, 500)
      });

      const saveMessage = saveResult.success 
        ? "✅ Analyse sauvegardée." 
        : "";

      // Ajouter un message à la conversation
      const analysisMessage = {
        id: `bot-analysis-${Date.now()}`,
        type: 'bot',
        text: `🎯 Analyse terminée ! Votre intention : "${analysisResults.intention}" 
Domaines : ${analysisResults.keyTerms?.join(', ') || 'Non définis'}

${saveMessage}

**Prochaines étapes :**
🤖 Propositions → conseils immédiats
🔍 Analyse → paramètres détaillés  
🌐 Recherche 360° → sources spécialisées

💬 Vous pouvez aussi continuer notre conversation pour affiner votre objectif !`
      };

      const updatedMessages = [...(conversationData.messages || []), analysisMessage];
      onConversationUpdate({ 
        messages: updatedMessages,
        analysisCompleted: true 
      });

      return analysisResults;
    } catch (error) {
      console.error('Erreur lors de l\'analyse sémantique:', error);
      setError(`Erreur d'analyse: ${error.message}`);

      // Utiliser l'analyse de fallback
      const fallbackResults = SemanticAnalyzerService.generateAdvancedFallbackAnalysis(userObjective);

      onAnalysisUpdate({
        results: fallbackResults,
        completed: true
      });

      await saveSemanticAnalysis(fallbackResults);

      return fallbackResults;
    } finally {
      setIsLoading(false);
    }
  }, [userObjective, conversationData.messages, onAnalysisUpdate, onConversationUpdate]);

  // Détecter le trigger d'analyse depuis d'autres modules
  useEffect(() => {
    if (analysisData.triggerAnalysis && !analysisData.completed && !analysisAttempted.current) {
      // Nettoyer le trigger
      onAnalysisUpdate({ triggerAnalysis: false });
      // Lancer l'analyse
      performSemanticAnalysis();
    }
  }, [analysisData.triggerAnalysis, analysisData.completed, onAnalysisUpdate, performSemanticAnalysis]);

  // Fonction pour sauvegarder l'analyse sémantique
  const saveSemanticAnalysis = useCallback(async (analysisResults, conversationContext = null) => {
    if (!analysisResults || !competenceId) {
      console.warn('Données manquantes pour la sauvegarde');
      return { success: false, error: 'Données manquantes' };
    }

    setIsSaving(true);
    setSaveStatus(null);

    try {
      const timestamp = serverTimestamp();

      const analysisToSave = {
        analysisId: `analysis_${Date.now()}`,
        analysisCreatedAt: timestamp,
        analysisUpdatedAt: timestamp,
        
        semanticAnalysis: {
          objective: userObjective,
          results: {
            ...analysisResults,
            confidence: analysisResults.confidence || 0.8,
            keyTerms: analysisResults.keyTerms || [],
            intention: analysisResults.intention || '',
            context: analysisResults.context || {},
            parameters: analysisResults.parameters || {}
          },
          
          conversationContext: conversationContext || {
            messageCount: conversationData.messages?.length || 0,
            lastMessages: (conversationData.messages || []).slice(-5).map(m => ({
              type: m.type,
              text: m.text?.substring(0, 200) || '',
              timestamp: m.id
            }))
          },
          
          aiResponse: conversationData.aiResponse || null,
          aiResponseGenerated: conversationData.aiResponseGenerated || false,
          originalObjectiveForAI: conversationData.originalObjectiveForAI || null,
          relevantLinks: conversationData.relevantLinks || [],
          optimizedQuery: analysisData.optimizedQuery || null,
          status: 'completed',
          version: '1.0'
        }
      };

      const saveResult = await onSaveData(analysisToSave);

      if (saveResult.success) {
        setLastSaveTime(new Date().toLocaleTimeString());
        setSaveStatus('success');
        console.log('Analyse sémantique sauvegardée avec succès');
      } else {
        setSaveStatus('error');
      }

      return saveResult;

    } catch (error) {
      console.error('Erreur lors de la sauvegarde de l\'analyse:', error);
      setSaveStatus('error');
      return { success: false, error: error.message };
    } finally {
      setIsSaving(false);
      setTimeout(() => setSaveStatus(null), 3000);
    }
  }, [competenceId, userObjective, conversationData, analysisData.optimizedQuery, onSaveData]);

  // Mettre à jour un paramètre d'analyse
  const handleParamChange = useCallback(async (paramName, newValue) => {
    if (!analysisData.results) return;

    const updatedAnalysis = SemanticAnalyzerService.updateAnalysisParam(
      analysisData.results,
      paramName,
      newValue
    );

    onAnalysisUpdate({
      results: updatedAnalysis
    });

    await saveSemanticAnalysis(updatedAnalysis);
  }, [analysisData.results, onAnalysisUpdate, saveSemanticAnalysis]);

  // Modifier l'analyse (revenir à l'état de conversation)
  const handleEditAnalysis = useCallback(() => {
    onSwitchTab('objectif');

    const editMessage = {
      id: `bot-edit-${Date.now()}`,
      type: 'bot',
      text: "OK ! Que souhaitez-vous modifier dans votre objectif de recherche ?"
    };

    const updatedMessages = [...(conversationData.messages || []), editMessage];
    onConversationUpdate({ messages: updatedMessages });
  }, [onSwitchTab, conversationData.messages, onConversationUpdate]);

  // Valider l'analyse et préparer la recherche
  const handleValidateAnalysis = useCallback(async (customQuery = null) => {
    setIsLoading(true);
    setError(null);

    try {
      let query = customQuery;

      if (!query) {
        query = await SemanticAnalyzerService.generateOptimizedQuery(analysisData.results);
      }

      // Mettre à jour les données d'analyse
      onAnalysisUpdate({
        optimizedQuery: query,
        readyToSearch: true
      });

      const updatedAnalysis = {
        ...analysisData.results,
        optimizedQuery: query,
        status: 'ready_for_search'
      };

      await saveSemanticAnalysis(updatedAnalysis);

      const validateMessage = {
        id: `bot-validate-${Date.now()}`,
        type: 'bot',
        text: `🚀 Requête prête : "${query}"

Analyse sauvegardée ! Vous pouvez maintenant lancer la recherche 360°.`
      };

      const updatedMessages = [...(conversationData.messages || []), validateMessage];
      onConversationUpdate({ messages: updatedMessages });

      return { success: true, query };
    } catch (error) {
      console.error('Erreur lors de la validation:', error);
      setError(`Erreur lors de la génération de la requête: ${error.message}`);

      const errorMessage = {
        id: `bot-error-${Date.now()}`,
        type: 'bot',
        text: "Problème lors de la génération de la requête. Réessayez ?"
      };

      const updatedMessages = [...(conversationData.messages || []), errorMessage];
      onConversationUpdate({ messages: updatedMessages });

      return { success: false, error: error.message };
    } finally {
      setIsLoading(false);
    }
  }, [analysisData.results, onAnalysisUpdate, onConversationUpdate, conversationData.messages, saveSemanticAnalysis]);

  // Sauvegarder manuellement
  const handleManualSave = useCallback(async () => {
    if (!analysisData.results) return;

    const result = await saveSemanticAnalysis(analysisData.results);

    if (result.success) {
      const saveMessage = {
        id: `bot-save-${Date.now()}`,
        type: 'bot',
        text: "✅ Analyse sauvegardée !"
      };

      const updatedMessages = [...(conversationData.messages || []), saveMessage];
      onConversationUpdate({ messages: updatedMessages });
    }
  }, [analysisData.results, saveSemanticAnalysis, conversationData.messages, onConversationUpdate]);

  // Relancer l'analyse
  const handleRerunAnalysis = useCallback(() => {
    analysisAttempted.current = false;
    setError(null);
    onAnalysisUpdate({
      results: null,
      completed: false,
      optimizedQuery: '',
      readyToSearch: false,
      triggerAnalysis: false
    });
  }, [onAnalysisUpdate]);

  // Lancer l'analyse manuellement (depuis l'interface)
  const handleLaunchAnalysis = useCallback(() => {
    if (!analysisAttempted.current) {
      performSemanticAnalysis();
    }
  }, [performSemanticAnalysis]);

  // Indicateur de statut de sauvegarde
  const renderSaveStatus = () => {
    if (isSaving) {
      return (
        <div className="flex items-center text-sm text-blue-600">
          <Loader2 className="h-4 w-4 mr-1 animate-spin" />
          <span>Sauvegarde...</span>
        </div>
      );
    }

    if (saveStatus === 'success') {
      return (
        <div className="flex items-center text-sm text-green-600">
          <CheckCircle className="h-4 w-4 mr-1" />
          {lastSaveTime && <span>Sauvegardé à {lastSaveTime}</span>}
        </div>
      );
    }

    if (saveStatus === 'error') {
      return (
        <div className="flex items-center text-sm text-red-600">
          <RefreshCw className="h-4 w-4 mr-1" />
          <span>Erreur de sauvegarde</span>
        </div>
      );
    }

    return null;
  };

  return (
    <div className="space-y-6">
      {error && (
        <div className="mb-4 bg-red-50 border border-red-200 text-red-600 p-4 rounded-lg">
          <div className="flex items-center justify-between">
            <span>{error}</span>
            <button
              onClick={() => setError(null)}
              className="text-red-400 hover:text-red-600"
            >
              ×
            </button>
          </div>
        </div>
      )}

      {/* Interface de lancement d'analyse si pas encore complétée */}
      {!analysisData.completed && (
        <div className="bg-white rounded-lg border border-gray-200 overflow-hidden">
          <div className="bg-gradient-to-r from-blue-50 to-indigo-50 p-6 border-b border-blue-100">
            <div className="flex items-center justify-between">
              <div className="flex items-center">
                <Lightbulb className="h-5 w-5 mr-2 text-blue-600" />
                <h2 className="text-lg font-semibold text-blue-800">Analyse Sémantique</h2>
              </div>
            </div>
            <p className="text-sm text-blue-600 mt-1">
              Analysez votre objectif pour optimiser votre recherche
            </p>
          </div>
          
          <div className="p-6 text-center">
            <div className="mb-6">
              <Sparkles className="h-12 w-12 mx-auto text-blue-400 mb-3" />
              <h4 className="text-lg font-medium text-gray-800 mb-2">Analyse sémantique avancée</h4>
              <p className="text-gray-600 text-sm max-w-md mx-auto">
                Notre IA va analyser votre objectif pour identifier les mots-clés optimaux, 
                détecter votre intention de recherche et structurer votre approche.
              </p>
            </div>
            
            <button
              onClick={handleLaunchAnalysis}
              disabled={isLoading}
              className="px-6 py-3 bg-gradient-to-r from-blue-500 to-indigo-500 text-white rounded-lg font-medium flex items-center mx-auto hover:from-blue-600 hover:to-indigo-600 disabled:opacity-50 transition-all shadow-md hover:shadow-lg"
            >
              {isLoading ? (
                <Loader2 className="h-5 w-5 mr-2 animate-spin" />
              ) : (
                <Search className="h-5 w-5 mr-2" />
              )}
              {isLoading ? 'Analyse en cours...' : 'Lancer l\'analyse sémantique'}
            </button>
            
            <p className="text-xs text-gray-500 mt-3">
              L'analyse prend quelques secondes et sera automatiquement sauvegardée
            </p>
          </div>
        </div>
      )}

      {/* Affichage des résultats d'analyse si complétée */}
      {analysisData.completed && analysisData.results && (
        <div className="bg-white rounded-lg border border-gray-200 overflow-hidden">
          <div className="bg-gradient-to-r from-blue-50 to-indigo-50 p-6 border-b border-blue-100">
            <div className="flex items-center justify-between">
              <div className="flex items-center">
                <Lightbulb className="h-5 w-5 mr-2 text-blue-600" />
                <h2 className="text-lg font-semibold text-blue-800">Analyse Sémantique</h2>
              </div>
              <div className="flex items-center space-x-3">
                {renderSaveStatus()}
                {!isSaving && (
                  <button
                    onClick={handleManualSave}
                    className="flex items-center text-sm text-blue-600 hover:text-blue-800 px-3 py-1 rounded border border-blue-200 hover:bg-blue-50"
                  >
                    <Save className="h-4 w-4 mr-1" />
                    <span>Sauvegarder</span>
                  </button>
                )}
              </div>
            </div>
            <p className="text-sm text-blue-600 mt-1">
              Résultats de l'analyse et actions disponibles
              {lastSaveTime && ` • Dernière sauvegarde: ${lastSaveTime}`}
            </p>
          </div>
          
          <div className="p-6">
            <div className="space-y-6">
              {/* Affichage des résultats d'analyse */}
              <div className="bg-gradient-to-r from-blue-50 to-indigo-50 p-4 rounded-lg">
                <h3 className="text-lg font-semibold text-blue-800 mb-3">Résultats de l'analyse</h3>
                <div className="grid md:grid-cols-2 gap-4">
                  <div>
                    <p className="text-sm font-medium text-gray-700">Intention détectée :</p>
                    <p className="text-blue-700 font-medium">{analysisData.results.intention}</p>
                  </div>
                  <div>
                    <p className="text-sm font-medium text-gray-700">Mots-clés principaux :</p>
                    <p className="text-blue-700">{analysisData.results.keyTerms?.join(', ') || 'Non définis'}</p>
                  </div>
                </div>
                
                {/* Catégories et besoins d'information */}
                {analysisData.results.topicCategories && (
                  <div className="mt-3">
                    <p className="text-sm font-medium text-gray-700">Catégories :</p>
                    <p className="text-blue-700">{analysisData.results.topicCategories.join(', ')}</p>
                  </div>
                )}
                
                {analysisData.results.informationNeeds && (
                  <div className="mt-3">
                    <p className="text-sm font-medium text-gray-700">Besoins d'information :</p>
                    <p className="text-blue-700">{analysisData.results.informationNeeds.join(', ')}</p>
                  </div>
                )}
                
              </div>

              {/* Actions disponibles */}
              <div className="flex flex-wrap gap-3">
                <button
                  onClick={handleEditAnalysis}
                  className="px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 flex items-center"
                >
                  <MessageCircle className="h-4 w-4 mr-2" />
                  Modifier via conversation
                </button>
                
                <button
                  onClick={handleRerunAnalysis}
                  className="px-4 py-2 border border-orange-300 text-orange-700 rounded-lg hover:bg-orange-50 flex items-center"
                >
                  <RefreshCw className="h-4 w-4 mr-2" />
                  Relancer l'analyse
                </button>
                
                <button
                  onClick={() => handleValidateAnalysis()}
                  disabled={isLoading}
                  className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 flex items-center"
                >
                  {isLoading ? (
                    <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                  ) : (
                    <CheckCircle className="h-4 w-4 mr-2" />
                  )}
                  Valider et générer requête
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
      
      {/* Bouton de lancement de recherche */}
      {analysisData.readyToSearch && analysisData.optimizedQuery && (
        <div className="bg-white rounded-lg border border-gray-200 overflow-hidden">
          <div className="bg-gradient-to-r from-green-50 to-emerald-50 p-4 border-b border-green-100">
            <h3 className="text-lg font-semibold text-green-800 flex items-center">
              <CheckCircle className="h-5 w-5 mr-2 text-green-600" />
              Requête optimisée prête
            </h3>
            {lastSaveTime && (
              <p className="text-sm text-green-600 mt-1">
                Configuration sauvegardée à {lastSaveTime}
              </p>
            )}
          </div>
          
          <div className="p-6">
            <div className="bg-blue-50 p-4 rounded-lg mb-4">
              <p className="text-base font-medium text-gray-800">{analysisData.optimizedQuery}</p>
            </div>
            
            <button
              onClick={onLaunchSearch}
              className="w-full p-4 bg-gradient-to-r from-blue-500 to-indigo-500 text-white rounded-lg font-medium flex items-center justify-center hover:from-blue-600 hover:to-indigo-600"
            >
              <Search className="h-5 w-5 mr-2" />
              Lancer la recherche 360°
            </button>
          </div>
        </div>
      )}
      
      {/* Suggestion pour obtenir une réponse IA si pas encore fait */}
      {analysisData.completed && !conversationData.aiResponseGenerated && (
        <div className="bg-gradient-to-r from-purple-50 to-pink-50 p-4 rounded-lg border border-purple-100">
          <div className="flex items-center justify-between">
            <div className="flex items-center">
              <Zap className="h-5 w-5 text-purple-500 mr-2" />
              <div>
                <p className="text-purple-800 font-medium">Proposition disponible</p>
                <p className="text-purple-600 text-sm">Obtenez des conseils immédiats avant de continuer</p>
              </div>
            </div>
            <button
              onClick={() => onSwitchTab('ai_response')}
              className="text-purple-600 hover:text-purple-800 flex items-center text-sm font-medium"
            >
              <span>Obtenir réponse IA</span>
              <ExternalLink className="h-4 w-4 ml-1" />
            </button>
          </div>
        </div>
      )}
      
      {/* Informations de sauvegarde */}
      {analysisData.completed && (
        <div className="bg-gray-50 p-4 rounded-lg border border-gray-200">
          <div className="flex items-start space-x-3">
            <Save className="h-5 w-5 text-gray-400 flex-shrink-0 mt-0.5" />
            <div>
              <p className="text-sm font-medium text-gray-700">Sauvegarde automatique</p>
              <p className="text-xs text-gray-600 mt-1">
                Votre analyse est automatiquement sauvegardée. Vous la retrouverez lors de votre prochaine visite.
                {conversationData.aiResponseGenerated && " Votre réponse IA est également sauvegardée."}
              </p>
            </div>
          </div>
        </div>
      )}
      
      {/* Conseils avec liens vers ressources */}
      <div className="bg-blue-50 p-4 rounded-lg border border-blue-100">
        <div className="flex items-start space-x-3">
          <BookOpen className="h-5 w-5 text-blue-500 flex-shrink-0 mt-0.5" />
          <div className="flex-1">
            <p className="text-sm font-medium text-blue-800">💡 Optimiser votre recherche</p>
            <p className="text-xs text-blue-700 mt-1 mb-3">
              L'analyse sémantique identifie automatiquement votre intention et les mots-clés pertinents. 
              {!conversationData.aiResponseGenerated && " Pensez aussi à consulter l'onglet Propositions pour des conseils immédiats."}
              {" Vous pouvez retourner à la conversation pour discuter de votre analyse."}
            </p>
            
            {/* Ressources d'aide */}
            <div className="flex flex-wrap gap-2">
              <a
                href="https://support.google.com/websearch/answer/2466433"
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center text-xs text-blue-600 hover:text-blue-800 hover:underline"
              >
                <ExternalLink className="h-3 w-3 mr-1" />
                Guide Google Search
              </a>
              <a
                href="https://scholar.google.com/intl/fr/scholar/help.html"
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center text-xs text-blue-600 hover:text-blue-800 hover:underline"
              >
                <ExternalLink className="h-3 w-3 mr-1" />
                Aide Google Scholar
              </a>
            </div>
          </div>
        </div>
      </div>

      {/* Actions recommandées selon l'état */}
      <div className="bg-gradient-to-r from-indigo-50 to-blue-50 p-4 rounded-lg border border-indigo-100">
        <div className="flex items-start space-x-3">
          <Lightbulb className="h-5 w-5 text-indigo-500 flex-shrink-0 mt-0.5" />
          <div className="flex-1">
            <p className="text-sm font-medium text-indigo-800">Actions recommandées</p>
            <div className="mt-2 space-y-1">
              {!analysisData.completed && (
                <p className="text-xs text-indigo-700">
                  • Lancez l'analyse sémantique pour identifier automatiquement vos mots-clés et votre intention
                </p>
              )}
              {analysisData.completed && !analysisData.readyToSearch && (
                <p className="text-xs text-indigo-700">
                  • Validez l'analyse pour générer une requête optimisée
                </p>
              )}
              {analysisData.readyToSearch && (
                <p className="text-xs text-indigo-700">
                  • Votre requête est prête ! Lancez la recherche 360° pour obtenir des sources spécialisées
                </p>
              )}
              {!conversationData.aiResponseGenerated && (
                <p className="text-xs text-indigo-700">
                  • Obtenez une réponse IA immédiate dans l'onglet "Propositions"
                </p>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AnalysisManager;